import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useTitle } from '../../hooks/useTitle';
import { toast } from 'react-toastify';
import Spinner from '../../components/Spinner';
import { Kyc, ApiKyc } from '../../models/Kyc';

interface DonorInfo {
  id: number;
  name: string;
  userNo: string;
}

const AGE_RANGE_OPTIONS = [
  { value: '20s', label: '20代' },
  { value: '30s', label: '30代' },
  { value: '40s', label: '40代' },
  { value: '50s_plus', label: '50代以上' }
];

const MypageReviewsEdit = () => {
  const { donor_no } = useParams<{ donor_no: string }>();
  const navigate = useNavigate();
  const [ donorInfo, setDonorInfo ] = useState<DonorInfo | null>(null);
  const [ score, setScore ] = useState(3);
  const [ comment, setComment ] = useState('');
  const [ ageRange, setAgeRange ] = useState('30s');
  const [ kyc, setKyc ] = useState<Kyc>();
  const { get, post, loading } = useApi();

  useTitle('Ton Bébé | レビュー記入');

  useEffect(() => {
    get({
      endpoint: `/api/v1/donor_reviews/me`,
      params: {
        donor_no: donor_no as string
      },
      onSuccess: (res) => {
        if (!res.data || !res.data.donor) {
          toast.error("対象のドナーが見つかりませんでした");
          navigate('/mypage/reviews');
          return;
        }

        setDonorInfo({
          id: res.data.donor.id,
          name: res.data.donor.name,
          userNo: res.data.donor.user_no
        });

        if (res.data.review) {
          setScore(res.data.review.score);
          setComment(res.data.review.comment || '');
          setAgeRange(res.data.review.age_range);
        }
      },
      onError: (err) => {
        console.error("Error fetching donor review:", err);
        toast.error("ドナー情報の取得に失敗しました");
        navigate('/mypage/reviews');
      },
    });

    get({
      endpoint: `/api/v1/kycs/show`,
      onSuccess: (res) => {
        const apiKyc: ApiKyc = res.data;
        setKyc(new Kyc(apiKyc));
      },
      onError: (err) => {
        console.error("Error fetching KYC:", err);
      },
    });
  }, [ donor_no, get, navigate ]);

  const isCertified = kyc?.status === 'MAN_CERTIFIED';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isCertified) {
      toast.error("本人確認が完了していません");
      return;
    }

    if (!comment.trim()) {
      toast.error("コメントを入力してください");
      return;
    }

    if (comment.trim().length < 40 || comment.trim().length > 300) {
      toast.error("コメントは40文字以上300文字以内で入力してください");
      return;
    }

    if (!donorInfo) {
      toast.error("ドナー情報が見つかりません");
      return;
    }

    const body = {
      donor_id: donorInfo.id,
      score: score,
      comment: comment,
      age_range: ageRange
    };

    post({
      endpoint: '/api/v1/donor_reviews',
      body,
      onSuccess: () => {
        toast.success("レビューを投稿しました");
        navigate('/mypage/reviews');
      },
      onError: (err) => {
        toast.error("レビューの投稿に失敗しました");
      }
    });
  };

  if (!donorInfo) {
    return (
      <main className="sub">
        <section className="donee_reviews_edit pink">
          <Spinner centered={ true } color="pink" />
        </section>
      </main>
    );
  }

  return (
    <main className="sub">
      <section className="donee_reviews_edit pink">
        { !isCertified &&
          <Link to="/mypage/kyc" className="sub_title alert_msg">
            本人確認が完了していません。レビューを記入するには、本人確認を完了してください。
          </Link>
        }
        <h2 className="main_title">
          <span>レビュー記入</span>
        </h2>
        <div className="form">
          <dl className="graf_list pink">
            <dt>ドナー</dt>
            <dd className="align-center">
              <span>{ donorInfo.name }（NO: { donorInfo.userNo }）</span>
            </dd>
            <dt>評価</dt>
            <dd className="align-center">
              <div className="form-group">
                <div className="rating-input">
                  { [ 1, 2, 3, 4, 5 ].map(num => (
                    <label key={ num } className="rating-label">
                      <input
                        type="radio"
                        name="score"
                        value={ num }
                        checked={ score === num }
                        onChange={ () => setScore(num) }
                        disabled={ loading }
                      />
                      <span className="star">{ num <= score ? '★' : '☆' }</span>
                    </label>
                  ))}
                </div>
              </div>
            </dd>
            <dt>年齢</dt>
            <dd>
              <div className="select select01">
              <select
                id="ageRange"
                value={ ageRange }
                onChange={ (e) => setAgeRange(e.target.value) }
                disabled={ loading }
                required
              >
                { AGE_RANGE_OPTIONS.map(option => (
                  <option key={ option.value } value={ option.value }>
                    { option.label }
                  </option>
                ))}
              </select>
              </div>
            </dd>
            <dt>コメント</dt>
            <dd>
              <textarea
                id="comment"
                value={ comment }
                onChange={ (e) => setComment(e.target.value) }
                placeholder="ドナーについてのコメントを記入してください"
                rows={ 5 }
                disabled={ loading }
                required
              />
              <span>
                <span className="txt_pink">{ comment.trim().length } 文字</span> （40~300文字で記入してください）
              </span>
              <br/>
              <span>
                入力のヒント：<br/>・メッセージのやり取りの速さや丁寧さ<br/>・事前の説明や証明書類の提示<br/>・当日のマナーや安心感を得られたポイント など
              </span>
            </dd>
          </dl>
          <p>
            ※ レビュー内容は記入者のイニシャルと共にドナープロフィールに公開されます。<br/>
            ※ 個人情報や連絡先は記入しないでください。<br/>
            ※ トラブルの原因となりますので、誹謗中傷や過度な低評価はお控えください。<br/>
            ※ 内容により、運営にてレビューを削除する場合があります。<br/>
            ※ 悪質なドナーについてはレビューを記入せず、<a href="https://docs.google.com/forms/d/e/1FAIpQLScGv-6GgDw8_xLOmch7yv7UGm7wgr64T7DYwhxMjZx3p8qHSg/viewform">こちら</a>から運営に通報をしてください。<br/>
          </p>
          <input type="submit" value="投稿する" className="w_pinkbtn" onClick={ handleSubmit } disabled={ !isCertified || loading } />
          { !isCertified && <p className="kyc-message"><Link to="/mypage/kyc">こちら</Link>から本人確認を行なってください。</p> }
        </div>
      </section>
    </main>
  );
};

export default MypageReviewsEdit;
